import type * as React from 'react'
import { useState, useEffect } from 'react'
import type { JSX } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client/react'
import { useChannelsConnected } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import {
  ArrowLeftIcon,
  ChannelAvatar,
  Flex,
  IconButton,
  InfoIcon,
  Grid3x3Icon,
  PageHeader,
  PageLayout,
  Skeleton,
  Text,
  ToggleGroup,
  Tooltip,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import Grid from '~publish/legacy/grid/components/GridPreview'
import { isService } from '~publish/helpers/channels'
import { selectProfileById } from '~publish/legacy/profile-sidebar/selectors'
import { useAppSelector } from '~publish/legacy/store'
import { canPerform } from '~publish/utils/permissions/canPerform'

import styles from './ChannelShopGrid.module.css'

export enum Views {
  InstagramPreview = 'instagram-preview',
  ShopGrid = 'shop-grid',
}

export const BACKFILL_UPDATES_FOR_GRID = graphql(/* GraphQL */ `
  mutation BackfillUpdatesForGrid($input: BackfillUpdatesForGridInput!) {
    backfillUpdatesForGrid(input: $input) {
      ... on MutationSuccess {
        _empty
      }
    }
  }
`)

export const GET_CUSTOM_LINKS = graphql(/* GraphQL */ `
  query ShopGridCustomLinks($input: ShopGridCustomLinksInput!) {
    shopGridCustomLinks(input: $input) {
      color
      contrastColor
      links {
        id
        text
        url
      }
    }
  }
`)

type ChannelShopGridStyle = React.CSSProperties &
  Record<`--${string}`, string | number | undefined>

export const ChannelShopGrid = (): JSX.Element => {
  const channelId =
    useParams<{
      id: string
    }>().id || ''
  const [backfillUpdatesForGrid] = useMutation(BACKFILL_UPDATES_FOR_GRID)

  const profile = useAppSelector((state) => selectProfileById(state, channelId))
  const channels = useChannelsConnected()
  const hasWriteAccess = channels.some(
    (channel) =>
      channel.id === channelId && canPerform(channel, 'manageChannelSettings'),
  )

  const { data } = useQuery(GET_CUSTOM_LINKS, {
    variables: {
      input: {
        channelId,
      },
    },
  })

  const [searchParams] = useSearchParams()
  const initialView =
    searchParams.get('view') === Views.ShopGrid
      ? Views.ShopGrid
      : Views.InstagramPreview
  const [selectedView, setSelectedView] = useState(initialView)

  useEffect(() => {
    if (channelId) {
      void backfillUpdatesForGrid({ variables: { input: { channelId } } })
    }
  }, [backfillUpdatesForGrid, channelId])

  useEffect(() => {
    BufferTrackerReact.instagramGridPreviewPageLoaded({
      product: 'publish',
      clientName: 'publishWeb',
      channelId,
    })
  }, [channelId])

  const channelShopGridStyle: ChannelShopGridStyle = {
    '--page-layout-max-width': '932px',
  }

  return (
    <PageLayout>
      <PageHeader separator={false}>
        <PageHeader.Row>
          <IconButton
            as={Link}
            to={`/channels/${channelId}`}
            label="Back to Channel"
            tooltip="Back to Channel"
            variant="tertiary"
          >
            <ArrowLeftIcon />
          </IconButton>
          {profile ? (
            <PageHeader.TitleGroup leadingGap="comfortable">
              <PageHeader.TitleLeading>
                <ChannelAvatar
                  src={profile.avatar_https}
                  channel={
                    isService(profile.service) ? profile.service : 'instagram'
                  }
                  alt={`avatar for ${profile.formatted_username}`}
                  size="mid"
                  className={styles.channelAvatar}
                />
              </PageHeader.TitleLeading>
              <PageHeader.Title>{profile.formatted_username}</PageHeader.Title>
              <PageHeader.TitleAdornments>
                <span className={styles.infoIcon}>
                  <Tooltip content="This is an approximation of your post grid. It may differ on other devices, and natively posted images update every 24 hours.">
                    <InfoIcon />
                  </Tooltip>
                </span>
              </PageHeader.TitleAdornments>
              <PageHeader.TitleDescription>Grid</PageHeader.TitleDescription>
            </PageHeader.TitleGroup>
          ) : (
            <Flex align="center" direction="row" gap="md">
              <Skeleton rounded width={44} height={44} />
              <Flex direction="column" gap="2xs">
                <Skeleton rounded width={120} height={24} />
                <Skeleton rounded width={40} height={16} />
              </Flex>
            </Flex>
          )}
          <ToggleGroup
            value={selectedView}
            onChange={(value): void => {
              if (
                value === Views.InstagramPreview ||
                value === Views.ShopGrid
              ) {
                setSelectedView(value)

                BufferTrackerReact.instagramGridPreviewToggleView({
                  product: 'publish',
                  clientName: 'publishWeb',
                  channelId,
                  view: value,
                })
              }
            }}
            size="medium"
            className={styles.toggleGroup}
          >
            <ToggleGroup.Item
              value={Views.InstagramPreview}
              aria-label="Instagram Preview"
            >
              <Grid3x3Icon />
              <span>
                <span className={styles.togglePrefix}>Instagram </span>
                Preview
              </span>
            </ToggleGroup.Item>
            <ToggleGroup.Item value={Views.ShopGrid}>
              <Grid3x3Icon />
              Shop Grid
            </ToggleGroup.Item>
          </ToggleGroup>
        </PageHeader.Row>
      </PageHeader>
      <PageLayout.Container
        className={styles.content}
        style={channelShopGridStyle}
      >
        <Text color="subtle" className={styles.description}>
          This is an approximation of your post grid. It may differ on other
          devices. Natively posted images update every 24 hours.
        </Text>
        {profile && data?.shopGridCustomLinks && (
          <Grid
            selectedView={selectedView}
            profile={profile}
            hasWriteAccess={hasWriteAccess}
            customLinksDetails={{
              color: data.shopGridCustomLinks.color,
              contrastColor: data.shopGridCustomLinks.contrastColor,
              links: (data.shopGridCustomLinks.links ?? []).map((link) => ({
                id: String(link.id),
                text: link.text,
                url: String(link.url),
              })),
            }}
          />
        )}
      </PageLayout.Container>
    </PageLayout>
  )
}
